import { createMuiTheme } from '@material-ui/core/styles';

const appTheme = createMuiTheme({
  palette: {
    primary: {
      main: '#006274',
      light: '#4c909d',
      dark: '#2f4857',
      contrastText: '#fff',
    },
    secondary: {
      main: '#D0E1E3',
      light: '#e9eef2',
      dark: '#007c7f',
      contrastText: '#fff',
    },
    text: {
      primary: '#2f4857',
      secondary: '#006274',
      disabled: '',
    },
    background: {
      default: '#fff',
      paper: '#f2f7f8',
      light: '#e4edef',
      disabled: '#eef2f5',
    },
    common: {
      black: '#000',
      white: '#fff',
      gray: '#808080',
      red: '#f44336',
    },
    navbar: {
      main: '#2f4857',
      text: '#fff',
      activeText: '#68edcb',
    },
    session: {
      active: '#ff961c',
      inactive: '#86bc25',
    },
    status: {
      online: '#98d827',
      offline: '#FF5050',
    },
    brabs: {
      main: '#e5eff1',
    },
    lifecycleState: {
      pending: '#ebc05b',
      ready: '#6ccde5',
      operative: '#8beacd',
      out_of_order: '#ed5d58',
      inactive: '#276273',
    },
    actions: {
      main: '#68edcb',
      hover: '#4ead95',
    },
    datePicker: {
      primary: '#016375',
      saturday: '#ffa6a6',
    },
    mapClusters: {
      business: '#ff6228',
      home: '#fff',
    },
  },
  typography: {
    fontFamily: '"Roboto","Arial", sans-serif',
    fontWeight: 400,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    htmlFontSize: 12,
    h1: {
      fontSize: '2.2rem',
      fontWeight: 300,
    },
    h2: {
      fontSize: '1.2rem',
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1rem',
      textTransform: 'uppercase',
    },
    h4: {
      fontSize: '1rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '1rem',
      fontWeight: 500,
    },
    h6: {
      fontSize: '0.85rem',
      fontWeight: 500,
    },
    body1: {
      fontSize: '1rem',
      margin: '4px 0px 8px',
    },
    body2: {
      fontSize: '0.85rem',
      margin: '4px 0px 8px',
    },
  },
  general: {
    maxWidth: '1900px',
  },
});

const overrides = (theme) => ({
  MuiButton: {
    root: {
      fontSize: '1rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      borderRadius: 0,
      boxShadow: 'none',
      '&:hover': {
        boxShadow: 'none',
      },
      transition: theme.transitions.create(['color', 'border', 'background-color'], {
        easing: theme.transitions.easing.easeInOut,
        duration: theme.transitions.duration.short,
      }),
    },
    contained: {
      color: theme.palette.text.primary,
      backgroundColor: 'transparent',
      borderColor: theme.palette.primary.main,
      borderStyle: 'solid',
      borderWidth: 1,
      borderRadius: 4,
      padding: theme.spacing(1, 2),
      boxShadow: 'none',
      '&:hover': {
        color: theme.palette.primary.light,
        backgroundColor: 'transparent',
        borderColor: theme.palette.primary.light,
        boxShadow: 'none',
      },
      '&:active': {
        boxShadow: 'none',
      },
      '&$disabled': {
        color: theme.palette.text.primary,
        backgroundColor: 'transparent',
        opacity: '0.4',
      },
    },
    containedPrimary: {
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.main,
      '&:hover': {
        color: theme.palette.common.white,
        backgroundColor: theme.palette.primary.light,
        boxShadow: 'none',
      },
      '&:active': {
        boxShadow: 'none',
      },
      '&$disabled': {
        color: theme.palette.primary.contrastText,
        backgroundColor: theme.palette.primary.main,
        opacity: '0.4',
      },
    },
    containedSecondary: {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.secondary.light,
      border: 'none',
      '&:hover': {
        color: theme.palette.common.white,
        backgroundColor: theme.palette.primary.light,
        border: 'none',
        boxShadow: 'none',
      },
      '&:active': {
        boxShadow: 'none',
      },
      '&$disabled': {
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.secondary.light,
        border: 'none',
        opacity: '0.4',
      },
    },
    outlined: {
      borderRadius: 30,
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    outlinedSizeSmall: {
      fontSize: '0.8rem',
    },
    outlinedPrimary: {
      color: theme.palette.primary.main,
      border: `1px solid ${theme.palette.primary.main}`,
      '&:hover': {
        backgroundColor: 'transparent',
        color: theme.palette.primary.dark,
        border: `1px solid ${theme.palette.primary.dark}`,
      },
    },
    iconSizeSmall: {
      '& > *:first-child': {
        fontSize: '1rem',
      },
    },
  },
  MuiInputBase: {
    root: {
      fontSize: '1rem',
      margin: 0,
      padding: 0,
      color: theme.palette.text.primary,
    },
    input: {
      fontSize: '1rem',
      height: '1.6rem',
      lineHeight: 1,
      '&::placeholder': {
        color: theme.palette.primary.light,
      },
    },
  },
  MuiOutlinedInput: {
    root: {
      '&:hover $notchedOutline': {
        borderColor: theme.palette.secondary.dark,
      },
      '&$focused $notchedOutline': {
        borderColor: theme.palette.secondary.dark,
        borderWidth: 1,
      },
    },
    notchedOutline: {
      borderColor: theme.palette.secondary.main,
    },
  },
  MuiFab: {
    root: {
      boxShadow: 'none',
    },
  },
  MuiChip: {
    root: {
      fontSize: theme.typography.body2.fontSize,
      color: theme.palette.secondary.dark,
      backgroundColor: theme.palette.background.paper,
      height: 26,
    },
    outlined: {
      border: `1px solid ${theme.palette.secondary.dark}`,
      backgroundColor: 'transparent',
    },
    label: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: theme.spacing(0, 2),
      '& > svg': {
        marginRight: theme.spacing(1),
        fontSize: '1.4em',
      },
    },
    deleteIcon: {
      width: 12,
      height: 12,
    },
  },
  MuiPaper: {
    elevation1: {
      boxShadow: 'none',
    },
  },
  MuiAccordion: {
    root: {
      backgroundColor: theme.palette.background.default,
      borderTop: `1px solid ${theme.palette.primary.main}`,
      transition: theme.transitions.create(['border', 'background-color'], {
        easing: theme.transitions.easing.easeInOut,
        duration: theme.transitions.duration.fast,
      }),
      '&:last-of-type': {
        borderBottom: `1px solid ${theme.palette.primary.main}`,
      },
      '&$expanded': {
        backgroundColor: theme.palette.background.paper,
        borderTop: `2px solid ${theme.palette.primary.main}`,
        margin: 0,
      },
    },
  },
  MuiAccordionSummary: {
    root: {
      padding: theme.spacing(0, 2),
      minHeight: 40,
      '&$expanded': {
        minHeight: 40,
      },
    },
    content: {
      color: theme.palette.primary.light,
      margin: theme.spacing(2, 0),
      '&$expanded': {
        color: theme.palette.primary.main,
        margin: theme.spacing(2, 0),
      },
    },
    expandIcon: {
      color: theme.palette.primary.main,
      padding: theme.spacing(1, 1),
    },
  },
  MuiAccordionDetails: {
    root: {
      padding: theme.spacing(1.5, 2, 3),
    },
  },
  MuiTabs: {
    root: {
      background: theme.palette.background.default,
    },
    indicator: {
      height: theme.spacing(0.5),
      backgroundColor: theme.palette.primary.main,
    },
  },
  MuiTab: {
    root: {
      minWidth: 80,
      color: theme.palette.primary.light,
      fontSize: theme.typography.body1.fontSize,
      fontWeight: theme.typography.fontWeightBold,
      textTransform: 'uppercase',
      padding: theme.spacing(0, 2),
      [theme.breakpoints.up('sm')]: {
        minWidth: 120,
        fontSize: theme.typography.body1.fontSize,
        padding: theme.spacing(0, 3),
      },
      transition: theme.transitions.create(['color'], {
        easing: theme.transitions.easing.easeInOut,
        duration: theme.transitions.duration.short,
      }),
    },
    textColorInherit: {
      opacity: 1,
      color: theme.palette.primary.light,
      '&:hover': {
        color: theme.palette.primary.main,
      },
      '&$selected': {
        color: theme.palette.primary.main,
      },
    },
  },
  PrivateTabScrollButton: {
    root: {
      width: 30,
    },
  },
  MuiTableCell: {
    root: {
      fontSize: '1rem',
      borderBottom: 'none',
    },
    head: {
      color: theme.palette.primary.light,
      paddingBottom: '10px',
    },
    body: {
      color: theme.palette.text.secondary,
      backgroundColor: theme.palette.common.white,

      '&:first-child': {
        borderTopLeftRadius: 5,
        borderBottomLeftRadius: 5,
      },

      '&:last-child': {
        borderTopRightRadius: 5,
        borderBottomRightRadius: 5,
      },
    },
  },
  MuiTableRow: {
    root: {
      minHeight: theme.spacing(9),
    },
    head: {
      backgroundColor: 'transparent',
      color: theme.palette.primary.light,
    },
  },
  MuiTableSortLabel: {
    root: {
      '&$active': {
        color: theme.palette.primary.light,
      },
    },
    icon: {
      height: '0.7em',
    },
  },
  MuiCheckbox: {
    colorSecondary: {
      '&$checked': {
        color: theme.palette.primary.main,
      },
    },
  },
  MuiIconButton: {
    root: {
      color: theme.palette.secondary.main,
    },
  },
  MuiTable: {
    root: {
      borderCollapse: 'separate',
      borderSpacing: theme.spacing(0, 1),
    },
  },
  MuiTablePagination: {
    caption: {
      fontSize: theme.typography.htmlFontSize,
      margin: 0,
    },
    select: {
      fontSize: theme.typography.htmlFontSize,
    },
    selectIcon: {
      top: 'initial',
    },
  },
  MuiSelect: {
    outlined: {
      ...theme.typography.h3,
      border: `1px solid ${theme.palette.primary.main}`,
      borderRadius: 30,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center !important', // that's hacky but we need it
      height: 'auto !important', // that's hacky but we need it
      '& > .MuiSvgIcon-root': {
        width: '0.7em',
      },
      '&$select:focus': {
        borderRadius: 30,
        backgroundColor: 'inherit',
      },
    },
    select: {
      '&:focus': {
        backgroundColor: 'inherit',
      },
    },
    selectMenu: {
      '&.MuiSelect-selectMenu.MuiSelect-selectMenu': {
        height: '1.6rem',
        display: 'inline-flex',
        alignItems: 'flex-end',
        paddingRight: theme.spacing(4),
      },
    },
  },
  MuiMenu: {
    paper: {
      backgroundColor: theme.palette.background.default,
    },
  },
  MuiMenuItem: {
    root: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      color: theme.palette.primary.light,

      '& > .MuiSvgIcon-root': {
        width: '0.7em',
      },
    },
  },
  MuiDialog: {
    paper: {
      backgroundColor: theme.palette.background.white,
    },
  },
  MuiDialogTitle: {
    root: {
      textAlign: 'center',
      padding: theme.spacing(4, 3, 2),
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(5, 3, 3),
      },
    },
  },
  MuiDialogContent: {
    root: {
      margin: theme.spacing(0),
      padding: theme.spacing(0, 3, 1),
      [theme.breakpoints.up('sm')]: {
        margin: theme.spacing(0, 8, 1),
      },
    },
  },
  MuiDialogActions: {
    root: {
      justifyContent: 'center',
      margin: theme.spacing(1, 2, 2),
      [theme.breakpoints.up('sm')]: {
        margin: theme.spacing(1, 4, 2),
      },
    },
    spacing: {
      '&> :not(:first-child)': {
        marginLeft: theme.spacing(3),
      },
    },
  },
  MuiFormGroup: {
    root: {
      position: 'relative',
    },
  },
  MuiFormHelperText: {
    root: {
      position: 'absolute',
      top: '100%',
      lineHeight: 1,
    },
  },
  MuiFormControlLabel: {
    label: {
      margin: 0,
    },
  },
  MuiDivider: {
    light: {
      backgroundColor: theme.palette.common.white,
    },
  },
  MuiStepper: {
    horizontal: {
      backgroundColor: 'transparent',
      padding: theme.spacing(0, 3),
    },
  },
  MuiStep: {
    horizontal: {
      minHeight: theme.spacing(3),
    },
    completed: {
      padding: 0,
    },
  },
  MuiStepLabel: {
    iconContainer: {
      paddingRight: 0,
    },
  },
  MuiAutocomplete: {
    paper: {
      background: theme.palette.background.default,
      boxShadow: theme.shadows[1],
    },
    option: {
      color: theme.palette.primary.light,
      minHeight: 40,
      '&[data-focus="true"]': {
        backgroundColor: theme.palette.background.paper,
      },
      [theme.breakpoints.up('sm')]: {
        minHeight: 40,
      },
    },
  },
});

const customShadows = (theme) => {
  const { shadows } = theme;
  shadows[1] = '0 1px 3px 0 rgba(0, 0, 0, 0.2)';
  shadows[2] = '0 14px 28px 0 rgba(0, 0, 0, 0.25)';
  shadows[8] = '0 1px 3px 0 rgba(0, 0, 0, 0.2)';
  shadows[24] = '0 14px 28px 0 rgba(0, 0, 0, 0.25)';
  return shadows;
};

export default createMuiTheme({
  ...appTheme,
  shadows: customShadows(appTheme),
  overrides: overrides(appTheme),
});
